
import {
  reactive, defineComponent, SetupContext, watch, computed, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import StatsBuilderFilters from '@/components/stats-builder/StatsBuilderFilters.vue';
import { useStore } from '@/store';
import moment from 'moment/moment';
import { formatDateToLocale } from '@/helpers/Date';

export default defineComponent({
  name: 'StatsFilterOnView',
  components: {
    StatsBuilderFilters,
    SpmButton,
    SpmOverlayPanel,
  },

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },

  emits: ['update:modelValue', 'apply-filters'],

  setup(props: { modelValue: any }, { emit }: SetupContext) {
    const { t, locale } = useI18n();
    const store = useStore();
    const filtersData = reactive(props.modelValue);
    const appliedFilters = computed(() => store.getters['statsEditor/getPageFilters']);
    const nbOtherAppliedFilters = computed(() => Object.keys(appliedFilters.value)
      .filter((key) => key !== 'date_interval' && appliedFilters.value[key].value && appliedFilters.value[key].value.length > 0).length);
    const appliedDateInterval = computed(() => {
      let returnValue;

      if (appliedFilters.value.date_interval.value.interval === 'customDateRange') {
        const startDate = moment(appliedFilters.value.date_interval.value.customDateRange[0]);
        const endDate = moment(appliedFilters.value.date_interval.value.customDateRange[1]);
        returnValue = `${formatDateToLocale(locale.value, startDate.format('YYYY-MM-DD HH:mm:ss'))?.split(' ')[0]}
         - ${formatDateToLocale(locale.value, endDate.format('YYYY-MM-DD HH:mm:ss'))?.split(' ')[0]}`;
      } else {
        returnValue = t(`customDatePicker.${appliedFilters.value.date_interval.value.interval}`);
      }

      return returnValue;
    });

    // Toute mutation des filtres (date, multi-select…) doit déclencher
    // immédiatement le refresh du rapport. Émission synchrone d'`apply-filters`
    // ici plutôt que via la chaîne `filters-changed` du child qui passe par un
    // `await loadFiltersOptions()` — si le panel se ferme entre temps,
    // l'émission est perdue et il faut rouvrir le panel pour appliquer.
    //
    // Snapshot post-mount : les composants PrimeVue (MultiSelect, Dropdown)
    // peuvent normaliser leur v-model à l'init (null → '', etc.), ce qui
    // déclenche le watch sans intervention user. On compare donc à un
    // snapshot capturé une fois la phase de mount stabilisée — toute
    // mutation qui ramène l'état à la même empreinte est ignorée.
    let stableSnapshot: string | null = null;
    nextTick(() => {
      stableSnapshot = JSON.stringify(filtersData);
    });
    watch(filtersData, () => {
      emit('update:modelValue', filtersData);
      if (stableSnapshot === null) return;
      const current = JSON.stringify(filtersData);
      if (current === stableSnapshot) return;
      stableSnapshot = current;
      emit('apply-filters');
    });

    // Idem côté event `filters-changed` du child : `StatsBuilderFilters` a
    // un watch deep qui peut émettre lors des normalisations PrimeVue au
    // mount. On bloque l'émission tant que le snapshot n'est pas posé
    // (= phase de mount terminée).
    const applyFilters = () => {
      if (stableSnapshot === null) return;
      emit('apply-filters');
    };

    return {
      t,
      filtersData,
      nbOtherAppliedFilters,
      appliedDateInterval,
      applyFilters,
    };
  },
});
